/**
 * ConfidenceBadge
 *
 * Small inline badge showing a confidence band label and numeric score.
 * Band thresholds:
 *   90-100  VERIFIED  green   #22C55E
 *   75-89   STRONG    blue    #0078D4
 *   55-74   FAIR      amber   #F59E0B
 *   35-54   WEAK      orange  #E89652
 *    0-34   BROKEN    red     #EF4444
 */
import React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { semanticColors } from '../../theme/solution-theme';

/* ------------------------------------------------------------------ */
/*  Band resolution                                                    */
/* ------------------------------------------------------------------ */

interface Band {
  label: string;
  color: string;
}

// #0078D4 (classic Fluent "communication blue"), #F59E0B (amber), and #EF4444
// (red) are not in brand-config.json's palette — no token represents them, so
// they are left as raw literals to avoid changing these band colours.
function resolveBand(score: number): Band {
  if (score >= 90) return { label: 'VERIFIED', color: semanticColors.success };
  if (score >= 75) return { label: 'STRONG', color: '#0078D4' };
  if (score >= 55) return { label: 'FAIR', color: '#F59E0B' };
  if (score >= 35) return { label: 'WEAK', color: semanticColors.warning };
  return { label: 'BROKEN', color: '#EF4444' };
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    borderRadius: tokens.borderRadiusMedium,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  small: {
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    // 1px: no token below spacingVerticalXXS (2px) — left as-is.
    paddingTop: '1px',
    paddingBottom: '1px',
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
  },
  medium: {
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
});

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ConfidenceBadgeProps {
  /** Confidence score 0-100. */
  score: number;
  /** Visual size variant. @default 'medium' */
  size?: 'small' | 'medium';
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({
  score,
  size = 'medium',
}) => {
  const styles = useStyles();
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const band = resolveBand(clamped);

  return (
    <span
      className={`${styles.badge} ${size === 'small' ? styles.small : styles.medium}`}
      style={{ backgroundColor: band.color, color: tokens.colorNeutralForegroundStaticInverted }}
      title={`Confidence: ${clamped} ${band.label}`}
      aria-label={`Confidence score ${clamped}, ${band.label}`}
    >
      {clamped} {band.label}
    </span>
  );
};
